import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import { number, object } from 'prop-types';
import React, { memo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { neutral, petrol } from '../../../styles/mui/colors';
import useAppTheme from '../../../util/hooks/useAppTheme';
import useScreenSize from '../../../util/hooks/useScreenSize';
import { FormattedMessage } from '../../../util/reactIntl';
import IconFilter from '../../Icons/IconFilter';
import { redirectToURLWithModalState } from '../../PortalModalContainer/MobileMenu/MobileMenu.util';

const searchButtonWrapperStyle = {
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
};

const searchButtonStyle = {
  color: petrol.main,
  bgcolor: 'white',
  border: `1px solid ${neutral[200]}`,
  '&:hover': {
    bgcolor: 'white',
  },
  '&:active': {
    bgcolor: 'white',
  },
  '&:focus': {
    bgcolor: 'white',
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
  },
  width: '43.8px',
};

const filterButtonStyle = {
  borderRadius: 200,
  bgcolor: 'white',
  borderColor: neutral[200],
  '&:hover': {
    bgcolor: 'white',
  },
  '&:active': {
    bgcolor: 'white',
  },
  '&:focus': {
    bgcolor: 'white',
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
  },
  color: 'inherit',
};

const topSearchBarMapStyle = (theme) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  p: 2,
  mb: 2,
  bgcolor: 'none',
  position: 'fixed',
  top: 0,
  zIndex: theme.globals.zIndexMobileMapFilters,
});

const topSearchBarStyleDefault = (theme) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  p: 2,
  mb: 2,
  bgcolor: 'white',
  top: 0,
  left: 2,
  right: 2,
  borderBottom: `1px solid ${neutral[100]}`,
  width: 1,
  height: '77px',
  zIndex: theme.globals.zIndexMobileFilters,
});

const filterBoxStyle = (theme) => ({
  position: 'fixed',
  top: 115,
  left: 0,
  right: 0,
  bgcolor: 'background.paper',
  boxShadow: 3,
  p: 2,
  overflowY: 'auto',
  height: '95vh',
  paddingBottom: '320px',
  zIndex: theme.globals.zIndexFilterBox,
});

const applyButtonsBoxStyle = {
  position: 'fixed',
  bottom: '80px',
  left: 0,
  width: '100vw',
  p: 2,
  bgcolor: 'background.paper',
  boxShadow: 2,
  zIndex: 2,
};

/**
 * SearchFiltersMobile component renders a search filters panel for mobile devices.
 *
 * This component includes:
 * - A top search bar with sorting options, search button, and filter toggle button.
 * - A slide-in filter panel containing filter options, an apply filters button, and a reset button.
 * - Functionality to open and close the filters panel, apply filters, reset filters, and handle search operations.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The filter options to be rendered inside the panel.
 * @param {React.ReactNode} [props.sortByComponent] - Optional component for sorting options.
 * @param {boolean} props.listingsAreLoaded - Indicates if the listings are loaded.
 * @param {number} [props.resultsCount] - The total number of results found.
 * @param {number} props.perPage - The number of results per page.
 * @param {boolean} [props.searchInProgress] - Indicates if a search operation is in progress.
 * @param {number} [props.selectedFiltersCount] - The number of selected filters.
 * @param {object} props.urlQueryParams - Query parameters from the URL.
 * @param {function} props.onManageDisableScrolling - Callback function for managing scrolling.
 * @param {function} props.resetAll - Callback function to reset all filters.
 *
 * @returns {React.JSX.Element} The rendered component.
 */
const SearchFiltersMobileComponent = (props) => {
  const {
    children, // filters
    sortByComponent = null,
    listingsAreLoaded,
    resultsCount = null,
    perPage,
    searchInProgress = false,
    shouldShowSearchMap,
    selectedFiltersCount = 0,
    isStoryblok,
    isLandingPage,
  } = props;

  const { isMobile, isDesktop } = useScreenSize();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const theme = useAppTheme();

  const topSearchBarListingsStyle = {
    ...topSearchBarStyleDefault(theme),
    position: isStoryblok || isLandingPage ? 'relative' : 'fixed',
  };

  const [isFiltersOpenOnMobile, setIsFiltersOpenOnMobile] = useState(false);
  const [initialQueryParams, setInitialQueryParams] = useState(null);

  const openFilters = useCallback(() => {
    const { urlQueryParams } = props;

    if (isFiltersOpenOnMobile) {
      document.body.classList.remove('no-scroll');
      setIsFiltersOpenOnMobile(false);
    } else {
      document.body.classList.add('no-scroll');
      setInitialQueryParams(urlQueryParams);
      setIsFiltersOpenOnMobile(true);
    }
  }, [isFiltersOpenOnMobile, props.urlQueryParams]);

  const closeFilters = useCallback(() => {
    document.body.classList.remove('no-scroll');
    setIsFiltersOpenOnMobile(false);
  }, []);

  const resetAll = useCallback(
    (e) => {
      props.resetAll(e);
      setIsFiltersOpenOnMobile(false);

      if (e && e.currentTarget) {
        e.currentTarget.blur();
      }
    },
    [props.resetAll],
  );

  const handleMobileSearchOpen = useCallback(() => {
    redirectToURLWithModalState(navigate, location, 'mobileSearch');
  }, [navigate, location]);

  const perPageLabel =
    resultsCount > perPage
      ? intl.formatMessage(
        {
          id: 'SearchFiltersMobile.perPage',
        },
        { perPage: perPage },
      )
      : '';

  const showListingsLabel = intl.formatMessage(
    { id: 'SearchFiltersMobile.showListings' },
    { count: resultsCount },
  );

  const resultsFound = (
    <Typography variant="body1">
      <FormattedMessage
        id="SearchFiltersMobile.foundResults"
        values={{ count: resultsCount, perPage: perPageLabel }}
      />
    </Typography>
  );

  const noResults = isDesktop ? (
    <Typography variant="body2">
      <FormattedMessage id="SearchFiltersMobile.noResults" />
    </Typography>
  ) : null;

  const searchButton = (
    <Box sx={searchButtonWrapperStyle}>
      <IconButton
        aria-label="search button"
        onClick={handleMobileSearchOpen}
        sx={searchButtonStyle}
      >
        <SearchIcon />
      </IconButton>
    </Box>
  );

  const openFiltersButton = (
    <Box>
      <Badge badgeContent={selectedFiltersCount} color="primary" overlap="circular">
        <Button
          onClick={openFilters}
          size={isMobile ? 'small' : 'medium'}
          startIcon={<IconFilter />}
          sx={filterButtonStyle}
          variant="outlined"
        >
          <Typography variant="body1">
            <FormattedMessage id="SearchFiltersMobile.filtersButtonLabel" />
          </Typography>
        </Button>
      </Badge>
    </Box>
  );

  const applyFiltersButton = (
    <Box alignItems="center" display="flex" flexDirection="column" sx={applyButtonsBoxStyle}>
      <Button disabled={searchInProgress} fullWidth onClick={(e) => resetAll(e)} variant="text">
        <FormattedMessage id="SearchFiltersMobile.resetAll" />
      </Button>
      <Button
        disabled={searchInProgress}
        fullWidth
        loading={searchInProgress}
        onClick={closeFilters}
        size="large"
        sx={{ my: 1 }}
        variant="contained"
      >
        {showListingsLabel}
      </Button>
    </Box>
  );

  const topSearchBar = (
    <Box sx={shouldShowSearchMap ? topSearchBarMapStyle : topSearchBarListingsStyle}>
      <Box direction="row" display="flex" gap={1} sx={{ width: 1 }}>
        {!shouldShowSearchMap ? sortByComponent : null}
        {openFiltersButton}
        {searchButton}
      </Box>
    </Box>
  );

  const filtersSlider = (
    <Slide direction="up" in={isFiltersOpenOnMobile} mountOnEnter unmountOnExit>
      <Box sx={filterBoxStyle}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography mb={2} variant="h5">
            <FormattedMessage id="SearchFiltersMobile.heading" />
          </Typography>
          <IconButton onClick={openFilters}>
            <CloseIcon />
          </IconButton>
        </Box>
        {children} {/* Filters */}
        {applyFiltersButton}
      </Box>
    </Slide>
  );

  return (
    <>
      <Box display="flex" height="77px" justifyContent="flex-end" mb={1}>
        {topSearchBar}
        {filtersSlider}
      </Box>

      {/* Search Results Indicator */}
      <Container sx={{ mb: 2 }}>
        {!shouldShowSearchMap && listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
        {!shouldShowSearchMap && listingsAreLoaded && resultsCount === 0 ? noResults : null}
      </Container>
    </>
  );
};

SearchFiltersMobileComponent.displayName = 'SearchFiltersMobile';

const SearchFiltersMobile = memo(SearchFiltersMobileComponent);

export default SearchFiltersMobile;
